import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { OarTaskApi } from '../../apis/oar-task-api';
import { ComparisonPageTaskApi } from '../../apis/comparison-page-task-api';
import { StatementOpinionAsyncApi } from '../../apis/statement-opinion-async-api';
import type { OarTaskService, StatementComparisonOptions } from '../oar-task-service';
import type { ClaimAnalysisResponse, ClaimAnalysisResult } from '../../types/claim-analysis';
import { mapClaimAnalysisResponse, toApiFormatResults } from '../../mappers/claim-analysis-mapper';

/**
 * 默认实现的 OAR 任务服务
 *
 * 基于 OarTaskApi 的 getTaskInfo 方法获取 OAR 任务信息
 */
@injectable()
export class DefaultOarTaskService implements OarTaskService {
    constructor(
        @inject(OarTaskApi)
        private readonly _oarTaskApi: OarTaskApi,
        @inject(ComparisonPageTaskApi)
        private readonly _comparisonPageTaskApi: ComparisonPageTaskApi,
        @inject(StatementOpinionAsyncApi)
        private readonly _statementOpinionAsyncApi: StatementOpinionAsyncApi,
    ) {}

    /**
     * 生成陈述意见内容（异步任务模式）
     *
     * 该方法使用异步任务模式：
     * 1. 提交生成任务
     * 2. 轮询任务状态（每5秒）
     * 3. 任务完成后获取结果
     *
     * @param taskId - 任务标识符
     * @param claimAnalysisResults - 权利要求分析结果数组
     * @param signal - 可选的 AbortSignal，用于取消轮询
     * @returns 生成的陈述意见内容（字符串）
     * @throws Error 当参数无效或任务失败时
     */
    // eslint-disable-next-line complexity
    async generateStatementContent(
        taskId: string,
        claimAnalysisResults: ClaimAnalysisResult[],
        signal?: AbortSignal,
    ): Promise<string> {
        // 参数校验
        if (!taskId) {
            throw new Error('taskId is required');
        }

        // 将前端业务模型转换为 API 格式
        const apiFormatResults = toApiFormatResults(claimAnalysisResults);

        // 1. 提交异步生成任务
        const submitResponse = await this._statementOpinionAsyncApi.generateStatementOpinionAsync({
            body: {
                task_id: taskId,
                analyze_opinions_result: apiFormatResults,
            },
        });

        const asyncTaskId = submitResponse.async_task_id;

        // 2. 轮询任务状态直到完成
        while (!signal?.aborted) {
            // 查询任务状态
            const statusData = await this._statementOpinionAsyncApi.getStatementOpinionTaskStatus({
                path: {
                    async_task_id: asyncTaskId,
                },
            });

            // 如果任务已完成
            if (statusData.status === 'COMPLETED') {
                // 3. 获取生成结果
                const resultData = await this._statementOpinionAsyncApi.getStatementOpinionTaskResult({
                    path: {
                        async_task_id: asyncTaskId,
                    },
                });

                return resultData.result?.opinion_content || '';
            } else if (statusData.status === 'FAILED' || statusData.status === 'CANCELLED') {
                // 任务失败或取消
                const errorMessage = statusData.error_message || '陈述意见生成任务失败';
                throw new Error(errorMessage);
            }

            // 等待 5 秒后继续轮询
            try {
                await this._waitForDelay(5000, signal);
            } catch (err) {
                // 将底层的取消错误转换为统一的错误消息
                if (err instanceof Error && err.message.includes('abort')) {
                    throw new Error('陈述意见生成已取消');
                }
                throw err;
            }
        }

        // 如果被 AbortSignal 取消
        throw new Error('陈述意见生成已取消');
    }

    /**
     * 生成陈述书对照页内容（异步模式）
     *
     * 该方法使用异步任务模式：
     * 1. 提交对照页生成任务
     * 2. 轮询任务状态（每5秒）
     * 3. 任务完成后获取结果
     *
     * @param taskId - 任务标识符
     * @param options - 对照页生成选项
     * @param options.statementDocument - 陈述书全文
     * @param options.patentText - 专利文本（权利要求或说明书）
     * @param options.field - 专利文本类型 ('claim' | 'description')
     * @returns 生成的对照页内容（字符串）
     * @throws Error 当任务失败时
     */
    // eslint-disable-next-line complexity
    async generateStatementComparisonContent(
        taskId: string,
        options: StatementComparisonOptions,
    ): Promise<string> {
        // 参数校验
        if (!taskId) {
            throw new Error('taskId is required');
        }
        if (!options.statementDocument) {
            throw new Error('statementDocument is required');
        }

        // 1. 提交异步生成任务
        const submitResponse = await this._comparisonPageTaskApi.generateComparisonPageAsync({
            body: {
                task_id: taskId,
                statement_document: options.statementDocument,
                field: options.field,
            },
        });

        const asyncTaskId = submitResponse.async_task_id;

        // 2. 轮询任务状态直到完成
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // 查询任务状态
            const statusData = await this._comparisonPageTaskApi.getComparisonPageTaskStatus({
                path: {
                    async_task_id: asyncTaskId,
                },
            });

            // 如果任务已完成
            if (statusData.is_finished) {
                if (statusData.is_successful) {
                    // 3. 获取对照页内容结果
                    const resultData = await this._comparisonPageTaskApi.getComparisonPageTaskResult({
                        path: {
                            async_task_id: asyncTaskId,
                        },
                    });

                    return resultData.content;
                } else {
                    // 任务失败
                    const errorMessage = statusData.error_message || '对照页生成任务失败';
                    throw new Error(errorMessage);
                }
            }

            // 等待 5 秒后继续轮询
            await this._waitForDelay(5000);
        }
    }

    /**
     * 分析指定结论性意见的权利要求（异步模式）
     *
     * 该方法使用异步任务模式：
     * 1. 提交分析任务
     * 2. 轮询任务状态（每5秒）
     * 3. 任务完成后获取结果
     *
     * @param taskId - 任务标识符
     * @param conclusiveOpinionId - 结论性意见 ID
     * @param signal - 可选的 AbortSignal，用于取消轮询
     * @returns 权利要求分析响应
     * @throws Error 当任务失败或被取消时
     */
    // eslint-disable-next-line complexity
    async analyzeClaims(
        taskId: string,
        conclusiveOpinionId: string,
        signal?: AbortSignal,
    ): Promise<ClaimAnalysisResponse> {
        // 1. 提交异步分析任务
        const submitResponse = await this._oarTaskApi.submitClaimAnalysisTask({
            path: {
                task_id: taskId,
                conclusive_opinion_id: conclusiveOpinionId,
            },
        });

        const asyncTaskId = submitResponse.async_task_id;

        // 2. 轮询任务状态直到完成
        while (!signal?.aborted) {
            // 查询任务状态
            const statusData = await this._oarTaskApi.getClaimAnalysisTaskStatus({
                path: {
                    async_task_id: asyncTaskId,
                },
            });

            // 如果任务已完成
            if (statusData.is_finished) {
                if (statusData.is_successful) {
                    // 3. 获取分析结果
                    const resultData = await this._oarTaskApi.getClaimAnalysisTaskResult({
                        path: {
                            async_task_id: asyncTaskId,
                        },
                    });

                    // 使用 mapper 转换为前端业务模型
                    const mappedResult = mapClaimAnalysisResponse(resultData);

                    return mappedResult;
                } else {
                    // 任务失败
                    const errorMessage = statusData.error_message || '权利要求分析任务失败';
                    throw new Error(errorMessage);
                }
            }

            // 等待 5 秒后继续轮询
            try {
                await this._waitForDelay(5000, signal);
            } catch (err) {
                // 将底层的取消错误转换为统一的错误消息
                if (err instanceof Error && err.message.includes('abort')) {
                    throw new Error('权利要求分析已取消');
                }
                throw err;
            }
        }

        // 如果被 AbortSignal 取消
        throw new Error('权利要求分析已取消');
    }

    /**
     * 可中断的延迟函数
     *
     * @param ms - 延迟毫秒数
     * @param signal - 可选的 AbortSignal
     * @returns Promise，在延迟后或被中断时 resolve
     * @private
     */
    private _waitForDelay(ms: number, signal?: AbortSignal): Promise<void> {
        return new Promise((resolve, reject) => {
            // 如果已经被取消，立即 reject
            if (signal?.aborted) {
                reject(new Error('Operation aborted'));
                return;
            }

            // 设置延迟
            const timeoutId = setTimeout(() => {
                resolve();
            }, ms);

            // 监听 abort 事件
            const abortHandler = () => {
                clearTimeout(timeoutId);
                reject(new Error('Operation aborted'));
            };

            signal?.addEventListener('abort', abortHandler, { once: true });

            // 清理函数：延迟结束后移除监听器
            setTimeout(() => {
                signal?.removeEventListener('abort', abortHandler);
            }, ms);
        });
    }
}
